// skipcq: JS-W1028
import { useEffect, useState } from 'react';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { CRAWL_DISPATCHER_RUNS_COLLECTION } from '../utils/firestoreCollections';
import { toDateMaybe, toNumberOrZero } from '../utils/firestoreFieldConversions';

function isSameLocalCalendarDay(dateA, dateB) {
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
}

function formatRunAxisLabel(at) {
  const now = new Date();
  const timeStr = at.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const ageMs = now.getTime() - at.getTime();
  const olderThan24h = ageMs > 24 * 60 * 60 * 1000;
  const differentLocalDay = !isSameLocalCalendarDay(at, now);
  if (differentLocalDay || olderThan24h) {
    const dateStr = at.toLocaleDateString(undefined, {
      month: 'numeric',
      day: 'numeric',
    });
    return `${dateStr} ${timeStr}`;
  }
  return timeStr;
}

const DISPATCHER_RUNS_CHART_LIMIT = 24;

/**
 * Last 24 `crawlDispatcherRuns` by `startedAt` (oldest to newest in `rows` for time-series display).
 */
function useCrawlDispatcherRunsChart() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const recentRunsQuery = query(
      collection(db, CRAWL_DISPATCHER_RUNS_COLLECTION),
      orderBy('startedAt', 'desc'),
      limit(DISPATCHER_RUNS_CHART_LIMIT),
    );
    const unsub = onSnapshot(
      recentRunsQuery,
      (snapshot) => {
        const newestFirst = [];
        for (const docSnapshot of snapshot.docs) {
          const docData = docSnapshot.data();
          const startedAt =
            toDateMaybe(docData.startedAt) ?? toDateMaybe(docData.finishedAt);
          if (!startedAt) {
            continue;
          }
          newestFirst.push({
            id: docSnapshot.id,
            startedAt,
            label: formatRunAxisLabel(startedAt),
            dispatchedCount: toNumberOrZero(docData.dispatchedCount),
            enqueueErrors: toNumberOrZero(docData.enqueueErrors),
          });
        }
        setRows(newestFirst.reverse());
        setLoading(false);
        setError(null);
      },
      (subscriptionError) => {
        console.error('useCrawlDispatcherRunsChart', subscriptionError);
        setError(subscriptionError);
        setRows([]);
        setLoading(false);
      },
    );
    return () => unsub();
  }, []);

  return { rows, loading, error };
}

export default useCrawlDispatcherRunsChart;
